import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Select } from '../components/Select';
import { Textarea } from '../components/Textarea';

interface FormErrors {
  agentId?: string;
  manuscriptId?: string;
  queryLetter?: string;
  synopsis?: string;
  firstPages?: string;
  authorBio?: string;
}

export const SubmitQueryPage = () => {
  const [step, setStep] = useState('form'); // 'form', 'preview', 'confirmation'
  const [formData, setFormData] = useState({
    agentId: '',
    manuscriptId: '',
    queryLetter: '',
    synopsis: '',
    firstPages: '',
    authorBio: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Mock data - in real app, would fetch from API
  const agents = [
    { value: '1', label: 'Rebecca Sterling - Sterling Literary Agency' },
    { value: '2', label: 'Marcus Chen - The Chen Agency' },
    { value: '5', label: 'Jennifer Wu - Wu & Partners Literary' },
  ].filter(agent => agent.value !== '3'); // Filter out closed agents

  const manuscripts = [
    { value: '1', label: 'Echoes of the Archive (Historical Fiction, 95k words)' },
    { value: '2', label: 'The Cartographer\'s Secret (Mystery, 82k words)' },
    { value: '3', label: 'Beneath Stone Bridges (Literary Fiction, 78k words)' },
  ];

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.agentId) {
      newErrors.agentId = 'Please select an agent';
    }

    if (!formData.manuscriptId) {
      newErrors.manuscriptId = 'Please select a manuscript';
    }

    if (!formData.queryLetter) {
      newErrors.queryLetter = 'Query letter is required';
    } else if (formData.queryLetter.split(' ').length < 100) {
      newErrors.queryLetter = 'Query letter should be at least 100 words';
    } else if (formData.queryLetter.split(' ').length > 500) {
      newErrors.queryLetter = 'Query letter should not exceed 500 words';
    }

    if (!formData.synopsis) {
      newErrors.synopsis = 'Synopsis is required';
    } else if (formData.synopsis.split(' ').length < 100) {
      newErrors.synopsis = 'Synopsis should be at least 100 words';
    } else if (formData.synopsis.split(' ').length > 750) {
      newErrors.synopsis = 'Synopsis should not exceed 750 words';
    }

    if (!formData.firstPages) {
      newErrors.firstPages = 'First pages are required';
    }

    if (!formData.authorBio) {
      newErrors.authorBio = 'Author bio is required';
    } else if (formData.authorBio.split(' ').length > 200) {
      newErrors.authorBio = 'Author bio should not exceed 200 words';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePreview = () => {
    if (validateForm()) {
      setStep('preview');
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = () => {
    console.log('Submitting query:', formData);
    setStep('confirmation');
    window.scrollTo(0, 0);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to discard this query?')) {
      window.location.href = '/agents';
    }
  };

  const wordCount = (text: string): number => {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  // Get selected agent and manuscript names for display
  const selectedAgent = agents.find(a => a.value === formData.agentId);
  const selectedManuscript = manuscripts.find(m => m.value === formData.manuscriptId);

  if (step === 'confirmation') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <Card style={{
            textAlign: 'center',
            padding: '60px 40px',
          }}>
            <div style={{
              fontSize: '60px',
              marginBottom: '24px',
            }}>
              ✓
            </div>
            <h1 style={{
              fontSize: '36px',
              fontWeight: '400',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              Query Submitted!
            </h1>
            <p style={{
              fontSize: '18px',
              lineHeight: '1.6',
              color: theme.colors.textSecondary,
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px',
            }}>
              Your query has been successfully submitted to {selectedAgent?.label.split(' - ')[0]}.
              You will receive an email confirmation shortly, and you can track the status
              of your query in your dashboard.
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
              <Button onClick={() => window.location.href = '/writer/queries'}>
                View My Queries
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/writer/dashboard'}>
                Go to Dashboard
              </Button>
            </div>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (step === 'preview') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Preview Your Query
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Review your query before submitting
            </p>
          </div>

          <Card style={{ marginBottom: '24px' }}>
            <div style={{
              backgroundColor: theme.colors.primaryLight,
              padding: '16px',
              borderRadius: theme.borderRadius.medium,
              marginBottom: '24px',
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: theme.colors.textSecondary,
                marginBottom: '8px',
              }}>
                Submitting to:
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
              }}>
                {selectedAgent?.label}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                marginTop: '8px',
              }}>
                Manuscript: {selectedManuscript?.label}
              </div>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}>
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  Query Letter ({wordCount(formData.queryLetter)} words)
                </h3>
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                }}>
                  {formData.queryLetter}
                </div>
              </div>

              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  Synopsis ({wordCount(formData.synopsis)} words)
                </h3>
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                }}>
                  {formData.synopsis}
                </div>
              </div>

              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  Author Bio ({wordCount(formData.authorBio)} words)
                </h3>
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                }}>
                  {formData.authorBio}
                </div>
              </div>

              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  First Pages (Preview)
                </h3>
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  fontSize: '14px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                  fontFamily: theme.fonts.monospace,
                  maxHeight: '300px',
                  overflow: 'auto',
                }}>
                  {formData.firstPages}
                </div>
              </div>
            </div>
          </Card>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
          }}>
            <Button variant="ghost" onClick={() => setStep('form')}>
              ← Back to Edit
            </Button>
            <Button onClick={handleSubmit}>
              Confirm and Submit Query
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Submit Query
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Submit your manuscript to a literary agent
          </p>
        </div>

        <Card style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}>
            {/* Agent Selection */}
            <Select
              label="Select Agent"
              value={formData.agentId}
              onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
              options={agents}
              placeholder="Choose an agent to query"
              error={errors.agentId}
              required
              helperText="Select the literary agent you wish to query"
            />

            {/* Manuscript Selection */}
            <Select
              label="Select Manuscript"
              value={formData.manuscriptId}
              onChange={(e) => setFormData({ ...formData, manuscriptId: e.target.value })}
              options={manuscripts}
              placeholder="Choose your manuscript"
              error={errors.manuscriptId}
              required
              helperText="Select the manuscript you're querying for"
            />

            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Query Letter */}
            <Textarea
              label="Query Letter"
              placeholder="Dear [Agent Name],

I am seeking representation for my [genre] manuscript, [TITLE], complete at [word count] words.

[Hook paragraph - introduce your book with the most compelling aspects]

[Plot paragraph 1 - set up the main character and situation]

[Plot paragraph 2 - develop the conflict and stakes]

[Comparison paragraph - comp titles and target audience]

[Author bio - relevant credentials, publications, or background]

Thank you for your time and consideration.

Sincerely,
[Your Name]"
              value={formData.queryLetter}
              onChange={(e) => setFormData({ ...formData, queryLetter: e.target.value })}
              rows={16}
              error={errors.queryLetter}
              helperText={`${wordCount(formData.queryLetter)} / 500 words (100 minimum) - Your pitch to the agent`}
              required
            />

            {/* Synopsis */}
            <Textarea
              label="Synopsis"
              placeholder="Provide a detailed synopsis of your manuscript, including the ending. This should cover the main plot points, character arcs, and resolution of your story..."
              value={formData.synopsis}
              onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
              rows={12}
              error={errors.synopsis}
              helperText={`${wordCount(formData.synopsis)} / 750 words (100 minimum) - Complete plot summary including ending`}
              required
            />

            {/* First Pages */}
            <Textarea
              label="First Pages"
              placeholder="Paste the first 5-10 pages of your manuscript here (or as specified by agent's guidelines)..."
              value={formData.firstPages}
              onChange={(e) => setFormData({ ...formData, firstPages: e.target.value })}
              rows={14}
              error={errors.firstPages}
              helperText="Your manuscript's opening pages - ensure proper formatting"
              required
            />

            {/* Author Bio */}
            <Textarea
              label="Author Bio"
              placeholder="Share relevant information about yourself as a writer. Include publications, awards, writing groups, relevant professional experience, or education. Keep it concise and professional..."
              value={formData.authorBio}
              onChange={(e) => setFormData({ ...formData, authorBio: e.target.value })}
              rows={6}
              error={errors.authorBio}
              helperText={`${wordCount(formData.authorBio)} / 200 words - Your writing credentials and background`}
              required
            />
          </div>
        </Card>

        {/* Help Card */}
        <Card style={{
          backgroundColor: theme.colors.backgroundAlt,
          marginBottom: '24px',
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Query Writing Tips
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            fontSize: '15px',
            lineHeight: '1.8',
            color: theme.colors.textSecondary,
          }}>
            <li>Personalize each query to the agent's specific interests and MSWL</li>
            <li>Keep your query letter concise - aim for 250-350 words</li>
            <li>Include comp titles published in the last 3-5 years</li>
            <li>Your synopsis should reveal the ending - agents need to know how your story concludes</li>
            <li>Proofread carefully - typos can hurt your chances</li>
            <li>Follow the agent's submission guidelines exactly</li>
          </ul>
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handlePreview}>
            Preview Query
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};
